import React, { createContext, useContext, useCallback, useRef, useState } from 'react';
import { TUTORIAL_STEPS, TutorialStep } from '@/tutorial/tutorialSteps';
import { analytics } from '@/services/analytics';
import { hasAnyStartedBook } from '@/utils/audioProgressUtils';

// Persists across sessions so the first-run tutorial only ever fires once.
// Bump the suffix if the tutorial changes enough that returning users should
// see it again. v2: steps rewritten for the reader with built-in read-along
// and Speak/Type entry control.
const STORAGE_KEY = 'aleph_tutorial_completed_v2';

/**
 * Master switch for the first-run tutorial. Off: the UI tested as clear enough
 * on its own, so nobody is walked through the player any more.
 *
 * The plumbing is left standing — steps, overlay, analytics, the completion
 * flag — so switching it back on is this one line.
 *
 * It is read by hasCompleted() rather than by start() alone, because callers
 * use hasCompleted() to decide whether to hold playback for the walkthrough
 * (see the Start/Continue handler in app.tsx). A tutorial that never starts
 * must not hold anything: gating start() by itself would leave first-run
 * playback waiting for an overlay that never appears.
 */
const TUTORIAL_ENABLED = false;

interface TutorialContextValue {
  isActive: boolean;
  stepIndex: number;
  totalSteps: number;
  /** The step currently being shown, or undefined when inactive. */
  step: TutorialStep | undefined;
  /** Whether the tutorial has already run (so it won't show again). */
  hasCompleted: () => boolean;
  /** Begin the tutorial. No-op if already completed or already running. */
  start: () => void;
  next: () => void;
  back: () => void;
  /** Dismiss early; counts as completed so it won't fire again. */
  skip: () => void;
}

const TutorialContext = createContext<TutorialContextValue | null>(null);

const hasCompleted = (): boolean => {
  // Reported as already seen while the tutorial is off, so every caller takes
  // the returning-listener path.
  if (!TUTORIAL_ENABLED) {
    return true;
  }

  try {
    if (localStorage.getItem(STORAGE_KEY) === 'true') {
      return true;
    }
  } catch {
    // If storage is unavailable, err on the side of not nagging the user.
    return true;
  }
  // Anyone already part-way through a book has been round this loop before —
  // they just did it on another device. The flag above is per-device
  // localStorage, so without this a fresh install (or a new browser) replays
  // first-run onboarding at an experienced listener.
  return hasAnyStartedBook();
};

/**
 * Record the tutorial as seen. Exported for callers that learn from the server
 * that a user is not a first-run listener before the local progress store has
 * caught up — see the native launch path in app.tsx.
 */
export const markTutorialCompleted = (): void => {
  try {
    localStorage.setItem(STORAGE_KEY, 'true');
  } catch {
    /* ignore */
  }
};

const markCompleted = markTutorialCompleted;

export const TutorialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isActive, setIsActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState<TutorialStep[]>(TUTORIAL_STEPS);
  // Mirror of `steps` for use inside the stable next() callback.
  const stepsRef = useRef<TutorialStep[]>(TUTORIAL_STEPS);
  // Mirror of `stepIndex`, kept in sync every render so next() can read the
  // current step without depending on it (which would change next()'s
  // identity on every step) and without computing it inside the setStepIndex
  // updater, where analytics.track()/finish() would rerun on React's
  // StrictMode double-invocation and double-fire "Tutorial Completed".
  const stepIndexRef = useRef(0);
  stepIndexRef.current = stepIndex;
  const isActiveRef = useRef(false);

  const start = useCallback(() => {
    if (isActiveRef.current || hasCompleted()) {
      return;
    }
    const visible = TUTORIAL_STEPS;
    stepsRef.current = visible;
    setSteps(visible);
    setStepIndex(0);
    isActiveRef.current = true;
    setIsActive(true);
    analytics.track('Tutorial Started');
    analytics.track('Tutorial Step Reached', {
      step_id: visible[0]?.id,
      step_index: 0,
      total_steps: visible.length,
    });
  }, []);

  const finish = useCallback(() => {
    markCompleted();
    isActiveRef.current = false;
    setIsActive(false);
    setStepIndex(0);
  }, []);

  const next = useCallback(() => {
    const i = stepIndexRef.current;
    if (i >= stepsRef.current.length - 1) {
      analytics.track('Tutorial Completed');
      finish();
      return;
    }
    const nextIndex = i + 1;
    analytics.track('Tutorial Step Reached', {
      step_id: stepsRef.current[nextIndex]?.id,
      step_index: nextIndex,
      total_steps: stepsRef.current.length,
    });
    setStepIndex(nextIndex);
  }, [finish]);

  const back = useCallback(() => {
    setStepIndex((i) => Math.max(0, i - 1));
  }, []);

  const skip = useCallback(() => {
    analytics.track('Tutorial Skipped', {
      step_id: stepsRef.current[stepIndex]?.id,
      step_index: stepIndex,
    });
    finish();
  }, [stepIndex, finish]);

  const value: TutorialContextValue = {
    isActive,
    stepIndex,
    totalSteps: steps.length,
    step: isActive ? steps[stepIndex] : undefined,
    hasCompleted,
    start,
    next,
    back,
    skip,
  };

  return <TutorialContext.Provider value={value}>{children}</TutorialContext.Provider>;
};

export const useTutorial = (): TutorialContextValue => {
  const ctx = useContext(TutorialContext);
  if (!ctx) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return ctx;
};
